export const ARTICLE_MERMAID_BLOT_NAME = 'article-mermaid';
export const ARTICLE_MERMAID_CLASS_NAME = 'article-mermaid';
export const ARTICLE_MERMAID_SOURCE_ATTRIBUTE = 'data-mermaid-src';
export const ARTICLE_MERMAID_TYPE_ATTRIBUTE = 'data-mermaid-type';
export const ARTICLE_MERMAID_LABEL_ATTRIBUTE = 'data-mermaid-label';

const ARTICLE_MERMAID_TYPE_PATTERNS = [
    { pattern: /^(?:flowchart|graph)\b/i, type: 'flowchart' },
    { pattern: /^sequencediagram\b/i, type: 'sequenceDiagram' },
    { pattern: /^classdiagram\b/i, type: 'classDiagram' },
    { pattern: /^statediagram(?:-v2)?\b/i, type: 'stateDiagram' },
    { pattern: /^erdiagram\b/i, type: 'erDiagram' },
    { pattern: /^journey\b/i, type: 'journey' },
    { pattern: /^gantt\b/i, type: 'gantt' },
    { pattern: /^pie\b/i, type: 'pie' },
    { pattern: /^gitgraph\b/i, type: 'gitGraph' },
    { pattern: /^mindmap\b/i, type: 'mindmap' },
    { pattern: /^timeline\b/i, type: 'timeline' },
    { pattern: /^quadrantchart\b/i, type: 'quadrantChart' },
    { pattern: /^xychart(?:-beta)?\b/i, type: 'xychart' },
    { pattern: /^sankey(?:-beta)?\b/i, type: 'sankey' },
    { pattern: /^requirementdiagram\b/i, type: 'requirementDiagram' },
    { pattern: /^c4context\b/i, type: 'C4Context' },
    { pattern: /^c4container\b/i, type: 'C4Container' },
    { pattern: /^c4component\b/i, type: 'C4Component' },
    { pattern: /^c4dynamic\b/i, type: 'C4Dynamic' },
    { pattern: /^c4deployment\b/i, type: 'C4Deployment' },
];

export function normalizeArticleMermaidSource(source) {
    if (typeof source !== 'string') {
        return '';
    }

    return source.replace(/\r\n?/g, '\n').trim();
}

export function encodeArticleMermaidSource(source) {
    return encodeURIComponent(normalizeArticleMermaidSource(source));
}

export function decodeArticleMermaidSource(encodedSource) {
    if (typeof encodedSource !== 'string' || encodedSource === '') {
        return '';
    }

    try {
        return decodeURIComponent(encodedSource);
    }
    catch (error) {
        return encodedSource;
    }
}

export function detectArticleMermaidTypeFromSource(source) {
    const normalizedSource = normalizeArticleMermaidSource(source);
    if (!normalizedSource) {
        return '';
    }

    const firstMeaningfulLine = normalizedSource
        .split('\n')
        .map((line) => line.trim())
        .find((line) => line !== '' && !line.startsWith('%%'));

    if (!firstMeaningfulLine) {
        return '';
    }

    const typeMatch = ARTICLE_MERMAID_TYPE_PATTERNS.find(({ pattern }) => pattern.test(firstMeaningfulLine));
    return typeMatch ? typeMatch.type : '';
}

export function buildArticleMermaidLabel(type) {
    return type ? `Mermaid diagram (${type})` : 'Mermaid diagram';
}

export function normalizeArticleMermaidValue(value) {
    let source = '';
    let type = '';

    if (typeof value === 'string') {
        source = normalizeArticleMermaidSource(value);
    }
    else if (value && typeof value === 'object') {
        source = normalizeArticleMermaidSource(value.source || decodeArticleMermaidSource(value.encodedSource || ''));
        type = typeof value.type === 'string' ? value.type.trim() : '';
    }

    if (!type) {
        type = detectArticleMermaidTypeFromSource(source);
    }

    return {
        source,
        type,
        label: buildArticleMermaidLabel(type),
    };
}

export function readArticleMermaidNodeValue(node) {
    if (!node || typeof node.getAttribute !== 'function') {
        return normalizeArticleMermaidValue(null);
    }

    return normalizeArticleMermaidValue({
        source: decodeArticleMermaidSource(node.getAttribute(ARTICLE_MERMAID_SOURCE_ATTRIBUTE) || ''),
        type: node.getAttribute(ARTICLE_MERMAID_TYPE_ATTRIBUTE) || '',
    });
}

export function writeArticleMermaidNodeAttributes(node, value) {
    if (!node || typeof node.setAttribute !== 'function') {
        return normalizeArticleMermaidValue(value);
    }

    const normalizedValue = normalizeArticleMermaidValue(value);

    node.setAttribute(ARTICLE_MERMAID_SOURCE_ATTRIBUTE, encodeArticleMermaidSource(normalizedValue.source));
    node.setAttribute(ARTICLE_MERMAID_LABEL_ATTRIBUTE, normalizedValue.label);

    if (normalizedValue.type) {
        node.setAttribute(ARTICLE_MERMAID_TYPE_ATTRIBUTE, normalizedValue.type);
    }
    else {
        node.removeAttribute(ARTICLE_MERMAID_TYPE_ATTRIBUTE);
    }

    return normalizedValue;
}
